import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

/**
 * Countdown strip shown when the tenant's licence is close to expiry.
 *
 * `expiring_soon` and `days_remaining` are both computed on the SERVER
 * (middleware/licenseGate.js) — the client never does date math against a
 * possibly-skewed browser clock, and the warn threshold stays a single env var
 * (LICENSE_WARN_DAYS) instead of a constant baked into this bundle. So this
 * component only has to render what it is told.
 *
 * Dismissal is per page load, not persisted: someone on their last trial day
 * should see it again tomorrow.
 */
const LicenseBanner = () => {
  const { license } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  if (!license?.expiring_soon || dismissed) return null;

  const days = license.days_remaining;
  const isTrial = license.plan === 'trial';

  const when = days === 0 ? 'today'
    : days === 1 ? 'tomorrow'
    : `in ${days} days`;

  // The last day reads as urgent rather than informational.
  const urgent = days <= 1;

  return (
    <div
      role="status"
      className={`flex items-center gap-3 px-4 py-2 text-sm border-b ${
        urgent
          ? 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900 text-red-800 dark:text-red-200'
          : 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900 text-amber-900 dark:text-amber-200'
      }`}
    >
      <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l2.5 2.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
      </svg>

      <span className="flex-1">
        {isTrial ? 'Your free trial ends' : 'Your Journey.AI subscription ends'}{' '}
        <span className="font-semibold">{when}</span>. Contact Journey.AI to continue without interruption.
      </span>

      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default LicenseBanner;
